import React from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import CalculationTooltip, { type TooltipBreakdownRow } from './CalculationTooltip.tsx';

interface ImpactMetricProps {
    title: React.ReactNode;
    /**
     * A plain-English gloss under the title, for readers without the adviser vocabulary. A client
     * meeting (2026-08-21) had a non-financial reader ask outright what "Cash Flow" and "Legacy
     * Estimate" meant: the concepts weren't hard, the labels were. Kept separate from `title`
     * because `title` is also interpolated into the tooltip heading as a string.
     */
    subtitle?: string;
    /**
     * The benchmark figure. `null` means the metric IS the benchmark — the plan summary showing the
     * walk-in position while the Suggestion Engine's cards carry the plans — so no grey figure, no
     * arrow and no change are drawn: one number, stated plainly.
     */
    beforeValue: number | null;
    afterValue: number;
    isLegacy?: boolean;
    /** Caption beside the delta, naming what the comparison is against (default "vs today"). */
    legacyCaption?: string;
    breakdown?: TooltipBreakdownRow[];
    /**
     * 'card' (default) is the hero tile — a white surface per figure, made to sit in a wide grid.
     *
     * 'row' is the same figure as one compact line, for the aged care plan summary panel. Four
     * hero tiles stacked in a ~391px pane is a lot of scrolling for four numbers (owner, 2026-08-26:
     * "the way you had it in the mock-up looks like it would take up less space because you are
     * going horizontal rather than taking up space going down"). The row keeps the before -> after
     * -> delta grammar and the breakdown tooltip exactly; only the geometry changes, which is why
     * this is a variant of THIS component rather than a second one that could drift from it.
     */
    layout?: 'card' | 'row';
}

/**
 * ImpactMetric — the hero results number (Age Pension, Cash Flow, Net Worth, Legacy).
 *
 * Redesigned (X4 in docs/UI_UX_HERO_PATH_AUDIT.md) to show the Before -> After story rather than only
 * the "after" value: muted before figure, arrow, bold after figure, and a clear signed delta with
 * direction + colour. White card on the canvas with a soft shadow so it reads as a primary surface.
 */
const ImpactMetric = ({ title, subtitle, beforeValue, afterValue, isLegacy, legacyCaption, breakdown, layout = 'card' }: ImpactMetricProps) => {
    const baselineOnly = beforeValue == null;
    const difference = afterValue - (beforeValue ?? afterValue);
    const isFlat = Math.round(difference) === 0;
    const isPositive = difference >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const deltaColor = isFlat ? 'text-slate-400' : isPositive ? 'text-emerald-600' : 'text-rose-600';

    if (layout === 'row') {
        return (
            <div className="flex items-baseline justify-between gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
                <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-1 text-[12px] font-bold uppercase tracking-wide text-slate-600">
                        {breakdown ? (
                            <CalculationTooltip title={`${title} Breakdown`} data={breakdown}>
                                <span className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">{title}</span>
                            </CalculationTooltip>
                        ) : (
                            <span>{title}</span>
                        )}
                    </span>
                    {subtitle && <span className="mt-0.5 block text-[13px] leading-snug text-slate-500">{subtitle}</span>}
                </span>
                {/* Right-aligned and tabular so the four rows read as a column of figures. */}
                <span className="shrink-0 text-right">
                    {!baselineOnly && <span className="block text-[13px] tabular-nums text-slate-500">{formatCurrency(beforeValue)} &rarr;</span>}
                    <span className="block text-lg font-bold leading-tight tabular-nums text-slate-900">{formatCurrency(afterValue)}</span>
                    {!baselineOnly && (
                        <span className={`flex items-center justify-end gap-1 text-[13px] font-semibold tabular-nums ${deltaColor}`}>
                            {!isFlat && <Icon size={12} className="flex-shrink-0" />}
                            {isFlat ? 'No change' : `${isPositive ? '+' : ''}${formatCurrency(difference)}`}
                            {(isLegacy || legacyCaption) && (
                                <span className="font-normal text-slate-400">{legacyCaption ?? 'vs today'}</span>
                            )}
                        </span>
                    )}
                </span>
            </div>
        );
    }

    return (
        /* EVERY ROW LINES UP WITH ITS NEIGHBOUR'S (owner-caught, 2026-08-26: "get numbered rows to
           match — it looks poorly done"). Side-by-side tiles only read as a comparison when the two
           headline figures sit on the same line, and two things broke that:

             1. the TITLE wrapped to two lines in one tile and one in the other ("Net Worth (Today)"
                beside "Legacy Estimate"), pushing everything below it down a line; and
             2. the before-arrow-after row wrapped only when the numbers happened to be wide enough,
                so "$27,830 -> $27,830" became two lines while "$830 -> $830" stayed one.

           Both are removed at the source rather than patched with a min-height guess. Title and
           subtitle each occupy a FIXED two lines (clamped, so long copy cannot push past it), and
           the figures no longer wrap by accident: the before value always sits on its own muted
           line above the hero number. The skeleton is therefore identical in every tile whatever
           the content, which also means it holds WITHOUT the parent grid having to cooperate - so
           it is right everywhere ImpactMetric is used, not only where someone remembered. */
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-card p-4 text-left h-full flex flex-col">
            <div className="flex min-h-[2rem] items-start gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {breakdown ? (
                    <CalculationTooltip title={`${title} Breakdown`} data={breakdown}>
                        <span className="line-clamp-2 cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">{title}</span>
                    </CalculationTooltip>
                ) : (
                    <span className="line-clamp-2">{title}</span>
                )}
            </div>
            <p className="mt-0.5 line-clamp-2 min-h-[2rem] text-xs font-normal normal-case tracking-normal text-slate-400">{subtitle ?? '\u00a0'}</p>

            {!baselineOnly && (
                <div className="mt-1 flex items-center gap-1 text-sm text-slate-400">
                    <span className="tabular-nums">{formatCurrency(beforeValue)}</span>
                    <ArrowRight size={13} className="flex-shrink-0 text-slate-300" />
                </div>
            )}
            <div className="text-2xl font-bold text-slate-900 tabular-nums">{formatCurrency(afterValue)}</div>

            {!baselineOnly && (
                <div className={`mt-auto pt-1 flex items-center gap-1 text-sm font-semibold ${deltaColor}`}>
                    {!isFlat && <Icon size={15} className="flex-shrink-0" />}
                    <span className="tabular-nums">
                        {isFlat ? 'No change' : `${isPositive ? '+' : ''}${formatCurrency(difference)}`}
                    </span>
                    {(isLegacy || legacyCaption) && <span className="text-xs font-normal text-slate-400 ml-1">{legacyCaption ?? 'vs today'}</span>}
                </div>
            )}
        </div>
    );
};

export default ImpactMetric;
